/**
 * E2EESetupModal
 * ─────────────────────────────────────────────────────────────────────────────
 * Shown automatically after login when E2EE key initialisation needs user input.
 *
 *   needs_setup   — New key generated. User creates a backup passphrase.
 *   needs_restore — Server backup found (new device). User enters passphrase.
 *
 * "Forgot my passkey?" flow (needs_restore only):
 *   Warns the user that old messages will be permanently unreadable, then
 *   generates a fresh key pair and transitions to the needs_setup screen so
 *   they can protect the new key with a passphrase.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setE2EEFlowStatus } from 'features/auth/authSlice'
import {
  encryptPrivateKeyWithPassphrase,
  decryptPrivateKeyWithPassphrase,
  generateKeyPair,
} from 'services/e2ee/cryptoService'
import { getPrivateKey, savePrivateKey, savePublicKey } from 'services/e2ee/keyStore'
import axios from 'axios'
import { baseUrl, config } from 'features/constants'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

export default function E2EESetupModal() {
  const { t } = useTranslation('messaging')
  const dispatch = useDispatch()
  const { e2eeFlowStatus, e2eeBackupData, user } = useSelector((s) => s.auth)
  const userId = user?._id

  const isSetup   = e2eeFlowStatus === 'needs_setup'
  const isRestore = e2eeFlowStatus === 'needs_restore'
  const visible   = isSetup || isRestore

  const [passphrase, setPassphrase] = useState('')
  const [confirm,    setConfirm]    = useState('')
  const [loading,    setLoading]    = useState(false)
  const [error,      setError]      = useState(null)
  const [done,       setDone]       = useState(false)
  // 'idle' | 'confirm' | 'generating'
  const [forgotStep, setForgotStep] = useState('idle')

  const dismiss = () => {
    setPassphrase('')
    setConfirm('')
    setError(null)
    setDone(false)
    setForgotStep('idle')
    dispatch(setE2EEFlowStatus({ status: 'idle', backup: null }))
  }

  // ── Setup: encrypt private key → upload backup ────────────────────────────
  const handleSetup = async () => {
    if (passphrase.length < 6) { setError(t('setup.minimumError')); return }
    if (passphrase !== confirm)  { setError(t('setup.mismatchError')); return }
    setError(null)
    setLoading(true)
    try {
      const privateKey = getPrivateKey(userId)
      if (!privateKey) throw new Error(t('setup.missingKeyError'))
      const backup = await encryptPrivateKeyWithPassphrase(privateKey, passphrase)
      await axios.post(`${baseUrl}/api/users/saveE2EEBackup`, backup, config(user?.token))
      setDone(true)
      setTimeout(dismiss, 1500)
    } catch (e) {
      setError(e?.message === t('setup.missingKeyError') ? e.message : t('setup.genericError'))
    } finally {
      setLoading(false)
    }
  }

  // ── Restore: decrypt backup → save key locally ────────────────────────────
  const handleRestore = async () => {
    if (!passphrase) { setError(t('setup.enterError')); return }
    if (!e2eeBackupData) { setError(t('setup.missingBackupError')); return }
    setError(null)
    setLoading(true)
    try {
      const privateKey = await decryptPrivateKeyWithPassphrase(e2eeBackupData, passphrase)
      if (!privateKey) { setError(t('setup.incorrectError')); setLoading(false); return }
      savePrivateKey(userId, privateKey)
      setDone(true)
      setTimeout(dismiss, 1500)
    } catch (e) {
      setError(t('setup.incorrectError'))
    } finally {
      setLoading(false)
    }
  }

  // ── Forgot passkey: generate new key pair → upload → switch to setup ──────
  const handleForgotKey = async () => {
    setForgotStep('generating')
    try {
      const { publicKey, privateKey } = generateKeyPair()
      savePrivateKey(userId, privateKey)
      savePublicKey(userId, publicKey)
      await axios.post(
        `${baseUrl}/api/users/uploadPublicKey`,
        { publicKey },
        config(user?.token)
      )
    } catch (e) {
      console.warn('[E2EE] Failed to upload new public key:', e)
    }
    setForgotStep('idle')
    // Switch to setup mode — user creates a passphrase for the brand-new key
    dispatch(setE2EEFlowStatus({ status: 'needs_setup', backup: null }))
  }

  if (!visible) return null

  // ── Forgot-passkey confirmation screen ────────────────────────────────────
  if (isRestore && forgotStep !== 'idle') {
    return (
      <Overlay>
        <Card>
          <IconCircle warn>⚠️</IconCircle>

          <Title>{t('setup.confirmTitle')}</Title>

          <BodyText>{t('setup.confirmBody')}</BodyText>

          <DangerBtn onClick={handleForgotKey} disabled={forgotStep === 'generating'}>
            {forgotStep === 'generating' ? t('setup.generating') : t('setup.generate')}
          </DangerBtn>

          <SkipBtn onClick={() => setForgotStep('idle')}>{t('setup.goBack')}</SkipBtn>
        </Card>
      </Overlay>
    )
  }

  return (
    <Overlay>
      <Card>
        <IconCircle isSetup={isSetup} done={done}>
          {done ? '✅' : isSetup ? '🔐' : '🔑'}
        </IconCircle>

        <Title>
          {done
            ? (isSetup ? t('setup.backupCreated') : t('setup.messagesRestored'))
            : isSetup
            ? t('setup.secureTitle')
            : t('setup.restoreTitle')}
        </Title>

        {!done && (
          <BodyText>
            {isSetup
              ? t('setup.setupBody')
              : t('setup.restoreBody')}
          </BodyText>
        )}

        {done && (
          <BodyText style={{ color: '#22c55e' }}>
            {isSetup
              ? t('setup.setupDone')
              : t('setup.restoreDone')}
          </BodyText>
        )}

        {!done && (
          <>
            <InputGroup>
              <Label>{isSetup ? t('setup.createPassphrase') : t('setup.enterPassphrase')}</Label>
              <Input
                type="password"
                value={passphrase}
                onChange={(e) => { setPassphrase(e.target.value); setError(null) }}
                placeholder={isSetup ? t('setup.minimumPlaceholder') : t('setup.backupPlaceholder')}
                autoComplete="new-password"
              />
            </InputGroup>

            {isSetup && (
              <InputGroup>
                <Label>{t('setup.confirmPassphrase')}</Label>
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => { setConfirm(e.target.value); setError(null) }}
                  placeholder={t('setup.repeatPlaceholder')}
                  autoComplete="new-password"
                />
              </InputGroup>
            )}

            {error && <ErrorText>⚠️ {error}</ErrorText>}

            <PrimaryBtn
              onClick={isSetup ? handleSetup : handleRestore}
              disabled={loading}
              isSetup={isSetup}
            >
              {loading ? t('setup.wait') : isSetup ? t('setup.saveBackup') : t('setup.restoreMessages')}
            </PrimaryBtn>

            {isSetup && (
              <SkipBtn onClick={dismiss}>
                {t('setup.skipSetup')}
              </SkipBtn>
            )}

            {isRestore && (
              <>
                <SkipBtn onClick={dismiss}>
                  {t('setup.skipRestore')}
                </SkipBtn>
                <ForgotBtn onClick={() => setForgotStep('confirm')}>
                  {t('setup.forgot')}
                </ForgotBtn>
              </>
            )}
          </>
        )}
      </Card>
    </Overlay>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
`

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  padding: 36px 32px;
  width: 100%;
  max-width: 440px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`

const IconCircle = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: ${(p) => p.done ? '#22c55e18' : p.warn ? '#ef444418' : p.isSetup ? '#3b82f618' : '#f9731618'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-bottom: 4px;
`

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  text-align: center;
  margin: 0;
  color: #111;
`

const BodyText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  text-align: center;
  color: #555;
  margin: 0;
`

const InputGroup = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Label = styled.label`
  font-size: 13px;
  font-weight: 500;
  color: #555;
`

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border-radius: 10px;
  border: 1.5px solid #e5e7eb;
  font-size: 15px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s;
  &:focus { border-color: #3b82f6; }
`

const ErrorText = styled.p`
  color: #ef4444;
  font-size: 13px;
  text-align: center;
  margin: 0;
`

const PrimaryBtn = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: ${(p) => p.isSetup ? '#3b82f6' : '#22c55e'};
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 4px;
  opacity: ${(p) => p.disabled ? 0.6 : 1};
  pointer-events: ${(p) => p.disabled ? 'none' : 'auto'};
  &:hover { filter: brightness(0.92); }
`

const SkipBtn = styled.button`
  background: none;
  border: none;
  color: #888;
  font-size: 12px;
  cursor: pointer;
  text-align: center;
  line-height: 1.5;
  padding: 4px 0;
  &:hover { color: #555; }
`

const DangerBtn = styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: #ef4444;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 4px;
  opacity: ${(p) => p.disabled ? 0.6 : 1};
  pointer-events: ${(p) => p.disabled ? 'none' : 'auto'};
  &:hover { filter: brightness(0.92); }
`

const ForgotBtn = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  padding: 4px 0;
  text-decoration: underline;
  &:hover { color: #dc2626; }
`
